import { IMagtekCardReaderCardData } from '../../typegen/i-magtek-card-reader-card-data';

export default class MagtekCardReaderCardData implements IMagtekCardReaderCardData {
	constructor(private rawDeviceData: MagTekKioDeviceData) {
		if (!rawDeviceData) throw Error('Magtek credit card data is null');
		this.rawDeviceData = rawDeviceData;
	}

	get firstName(): string {
		const nameSection = this.extractNameSection();
		if (!nameSection) return '';

		const nameParts = nameSection.split('/');
		return nameParts.length > 1 ? nameParts[1].trim() : '';
	}

	get lastName(): string {
		const nameSection = this.extractNameSection();
		if (!nameSection) return '';

		const nameParts = nameSection.split('/');
		return nameParts.length > 0 ? nameParts[0].trim() : '';
	}

	get cardNumber() {
		const masked1 = this.rawDeviceData.Masked1;
		if (!masked1) return '';

		// Extract card number from the beginning until the first ^ character
		// Format: %CARDNUMBER^LASTNAME/FIRSTNAME^EXPIRY?
		const cardNumberMatch = masked1.match(/^%(.+?)\^/);
		return cardNumberMatch ? cardNumberMatch[1] : '';
	}

	get maskedCardNumber(): string {
		const cardNumber = this.cardNumber;
		if (!cardNumber || cardNumber.length < 8) return '';

		// Show first 4 and last 4 digits, mask the middle with *
		const firstFour = cardNumber.substring(0, 4);
		const lastFour = cardNumber.substring(cardNumber.length - 4);
		const middleLength = cardNumber.length - 8;
		const maskedMiddle = '*'.repeat(middleLength);

		return `${firstFour}${maskedMiddle}${lastFour}`;
	}

	get unmaskedCardNumber(): string {
		// In this implementation, the full card number is already available in Masked1
		// This is the complete unmasked number
		return this.cardNumber;
	}

	get lastFourDigits(): string {
		const cardNumber = this.cardNumber;
		if (!cardNumber || cardNumber.length < 4) return '';

		return cardNumber.substring(cardNumber.length - 4);
	}

	get cardType(): string {
		const cardNumber = this.cardNumber;
		if (!cardNumber) return '';

		// Remove any spaces or dashes
		const cleanNumber = cardNumber.replace(/[\s-]/g, '');

		// Visa: starts with 4
		if (/^4\d{15}$/.test(cleanNumber)) {
			return 'Visa';
		}

		// Mastercard: starts with 5[1-5] or 2[2-7]
		if (/^5[1-5]\d{14}$/.test(cleanNumber) || /^2[2-7]\d{14}$/.test(cleanNumber)) {
			return 'Mastercard';
		}

		// American Express: starts with 34 or 37
		if (/^3[47]\d{13}$/.test(cleanNumber)) {
			return 'American Express';
		}

		// Discover: starts with 6011, 622[1-9], 64[4-9], or 65
		if (
			/^6011\d{12}$/.test(cleanNumber) ||
			/^622[1-9]\d{12}$/.test(cleanNumber) ||
			/^64[4-9]\d{13}$/.test(cleanNumber) ||
			/^65\d{14}$/.test(cleanNumber)
		) {
			return 'Discover';
		}

		// Diners Club: starts with 30[0-5], 36, or 38
		if (/^30[0-5]\d{11}$/.test(cleanNumber) || /^36\d{12}$/.test(cleanNumber) || /^38\d{12}$/.test(cleanNumber)) {
			return 'Diners Club';
		}

		// JCB: starts with 35
		if (/^35\d{14}$/.test(cleanNumber)) {
			return 'JCB';
		}

		return 'Unknown';
	}

	get expirationDate(): string {
		const masked1 = this.rawDeviceData.Masked1;
		if (!masked1) return '';

		// Extract expiration date from after the second ^ character
		// Format: %CARDNUMBER^LASTNAME/FIRSTNAME^YYMM...?
		const expiryMatch = masked1.match(/\^[^/^]+\/[^^]+\^(\d{4})/);
		if (!expiryMatch) return '';

		const yymm = expiryMatch[1];
		if (yymm.length !== 4) return '';

		const yy = yymm.substring(0, 2);
		const mm = yymm.substring(2, 4);

		return `${mm}/${yy}`;
	}

	get expirationMonth(): string {
		const masked1 = this.rawDeviceData.Masked1;
		if (!masked1) return '';

		// Extract expiration date from after the second ^ character
		// Format: %CARDNUMBER^LASTNAME/FIRSTNAME^YYMM...?
		const expiryMatch = masked1.match(/\^[^/^]+\/[^^]+\^(\d{4})/);
		if (!expiryMatch) return '';

		const yymm = expiryMatch[1];
		if (yymm.length !== 4) return '';

		return yymm.substring(2, 4); // MM
	}

	get expirationYear(): string {
		const masked1 = this.rawDeviceData.Masked1;
		if (!masked1) return '';

		// Extract expiration date from after the second ^ character
		// Format: %CARDNUMBER^LASTNAME/FIRSTNAME^YYMM...?
		const expiryMatch = masked1.match(/\^[^/^]+\/[^^]+\^(\d{4})/);
		if (!expiryMatch) return '';

		const yymm = expiryMatch[1];
		if (yymm.length !== 4) return '';

		return yymm.substring(0, 2); // YY
	}

	private extractNameSection(): string {
		const masked1 = this.rawDeviceData.Masked1;
		if (!masked1) return '';

		// Extract name section between the ^ characters
		// Format: %CARDNUMBER^LASTNAME/FIRSTNAME^EXPIRY?
		const nameMatch = masked1.match(/\^([^/^]+\/[^^]+)\^/);
		return nameMatch ? nameMatch[1] : '';
	}

	get invoiceCloudDataString(): string {
		const data = this.rawDeviceData;
		// Helper function to convert an array of integers to a hexadecimal string
		function arrayToHexString(array: number[]) {
			return array
				.map((byte) => byte.toString(16).padStart(2, '0'))
				.join('')
				.toUpperCase();
		}

		// Helper function to convert an array of integers to an ASCII string
		function arrayToAsciiString(array: number[]) {
			return array
				.map((byte) => String.fromCharCode(byte))
				.join('')
				.replace(/\0/g, ''); // Remove null characters
		}

		// Helper function to reverse the byte order of a 4-byte hexadecimal string
		function reverseHexBytes(hexString: string) {
			return hexString.match(/../g)?.reverse().join('');
		}

		// Convert MagnePrintStatus to a 4-byte hexadecimal string and reverse the byte order
		const magnePrintStatusHex = reverseHexBytes(data.MagnePrintStatus.toString(16).padStart(8, '0').toUpperCase());

		// Extract and convert fields
		const deviceSerialNumber = arrayToAsciiString(data.SerialNumber); // Convert to ASCII
		const keySerialNumber = arrayToHexString(data.DukptKsn);
		const magnePrintData = arrayToHexString(data.EncrMagnePrintData);
		const track2EncryptedData = arrayToHexString(data.Track2);

		// Create and return the final string
		return `K|${deviceSerialNumber}|${keySerialNumber}|${magnePrintData}|${magnePrintStatusHex}||${track2EncryptedData}|`;
	}

	/**
	 * Custom JSON serialization method that includes all getter properties.
	 * This method is automatically called by JSON.stringify().
	 */
	toJSON() {
		return {
			firstName: this.firstName,
			lastName: this.lastName,
			cardNumber: this.cardNumber,
			maskedCardNumber: this.maskedCardNumber,
			unmaskedCardNumber: this.unmaskedCardNumber,
			lastFourDigits: this.lastFourDigits,
			cardType: this.cardType,
			expirationDate: this.expirationDate,
			expirationMonth: this.expirationMonth,
			expirationYear: this.expirationYear,
			invoiceCloudDataString: this.invoiceCloudDataString,
		};
	}
}
